/**
 * WhatsApp Button Component for Puck Editor
 * 
 * Tombol floating atau inline untuk WhatsApp - penting untuk UMKM Indonesia
 */

import type { ComponentConfig } from '@measured/puck'

export interface WhatsAppButtonProps {
  phoneNumber: string
  message: string
  buttonText: string
  variant: 'floating' | 'inline' | 'full-width'
  position: 'bottom-right' | 'bottom-left'
  showIcon: boolean
}

// WhatsApp icon SVG
const WhatsAppIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
)

export function WhatsAppButton({
  phoneNumber,
  message,
  buttonText,
  variant,
  position,
  showIcon,
}: WhatsAppButtonProps) {
  // Clean phone number (remove spaces, dashes, etc.)
  const cleanNumber = phoneNumber?.replace(/\D/g, '') || ''

  // Validate phone number - show warning if invalid
  if (!cleanNumber) {
    return (
      <div className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-lg text-sm">
        ⚠️ Nomor WhatsApp tidak valid
      </div>
    )
  }

  const encodedMessage = encodeURIComponent(message || '')
  const waLink = `https://wa.me/${cleanNumber}?text=${encodedMessage}`

  if (variant === 'floating') {
    const positionClasses =
      position === 'bottom-right' ? 'right-4' : 'left-4'

    // Floating variant always shows icon for UX - the circular button
    // needs the icon to be recognizable as a WhatsApp action button
    return (
      <a
        href={waLink}
        target="_blank"
        rel="noopener noreferrer"
        className={`fixed bottom-4 ${positionClasses} z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-transform hover:scale-110`}
        aria-label="Chat WhatsApp"
      >
        <WhatsAppIcon />
      </a>
    )
  }

  if (variant === 'full-width') {
    return (
      <a
        href={waLink}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-3 w-full bg-green-500 hover:bg-green-600 text-white py-4 px-6 rounded-lg font-semibold transition-colors"
      >
        {showIcon && <WhatsAppIcon />}
        {buttonText}
      </a>
    )
  }

  // Inline variant
  return (
    <a
      href={waLink}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
    >
      {showIcon && <WhatsAppIcon />}
      {buttonText}
    </a>
  )
}

export const WhatsAppButtonConfig: ComponentConfig<WhatsAppButtonProps> = {
  label: 'Tombol WhatsApp',
  defaultProps: {
    phoneNumber: '6281234567890',
    message: 'Halo, saya tertarik dengan produk Anda',
    buttonText: 'Hubungi Kami',
    variant: 'inline',
    position: 'bottom-right',
    showIcon: true,
  },
  fields: {
    phoneNumber: {
      type: 'text',
      label: 'Nomor WhatsApp (dengan kode negara)',
    },
    message: {
      type: 'textarea',
      label: 'Pesan Default',
    },
    buttonText: {
      type: 'text',
      label: 'Teks Tombol',
    },
    variant: {
      type: 'select',
      label: 'Gaya',
      options: [
        { label: 'Inline', value: 'inline' },
        { label: 'Floating', value: 'floating' },
        { label: 'Lebar Penuh', value: 'full-width' },
      ],
    },
    position: {
      type: 'radio',
      label: 'Posisi (untuk Floating)',
      options: [
        { label: 'Kanan Bawah', value: 'bottom-right' },
        { label: 'Kiri Bawah', value: 'bottom-left' },
      ],
    },
    showIcon: {
      type: 'radio',
      label: 'Tampilkan Ikon',
      options: [
        { label: 'Ya', value: true },
        { label: 'Tidak', value: false },
      ],
    },
  },
  render: WhatsAppButton,
}
